import { BACKGROUND_TEXT_MODE } from '../../constants';

export const TEXT_BOX_BACKDROP_MIN = 0;

export function normalizeTextBoxBackdropValue(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return TEXT_BOX_BACKDROP_MIN;
  }

  return numericValue;
}

export function hasTextBoxBackdrop(element) {
  return (
    (element?.backgroundTextMode === BACKGROUND_TEXT_MODE.NONE ||
      element?.backgroundTextMode === BACKGROUND_TEXT_MODE.FILL) &&
    normalizeTextBoxBackdropValue(element.textBoxBackdropBlur) > 0
  );
}

function normalizeDimension(value) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function getCornerRadii(borderRadius, expansion, width, height) {
  const maxRadius = Math.min(width / 2, height / 2);
  const radius = (value) =>
    Math.min(normalizeDimension(value) + expansion, maxRadius);

  return {
    topLeft: radius(borderRadius?.topLeft),
    topRight: radius(borderRadius?.topRight),
    bottomRight: radius(borderRadius?.bottomRight),
    bottomLeft: radius(borderRadius?.bottomLeft),
  };
}

function getRoundedRectPath({ x, y, width, height, borderRadius }) {
  const right = x + width;
  const bottom = y + height;
  const { topLeft, topRight, bottomRight, bottomLeft } = borderRadius;
  const arc = (radius, endX, endY) =>
    radius > 0
      ? `A ${radius} ${radius} 0 0 1 ${endX} ${endY}`
      : `L ${endX} ${endY}`;

  return [
    `M ${x + topLeft} ${y}`,
    `H ${right - topRight}`,
    arc(topRight, right, y + topRight),
    `V ${bottom - bottomRight}`,
    arc(bottomRight, right - bottomRight, bottom),
    `H ${x + bottomLeft}`,
    arc(bottomLeft, x, bottom - bottomLeft),
    `V ${y + topLeft}`,
    arc(topLeft, x + topLeft, y),
    'Z',
  ].join(' ');
}

function getSoftEdgeMask(element, softness) {
  const width = normalizeDimension(element.width);
  const height = normalizeDimension(element.height);
  const maskWidth = width + softness * 4;
  const maskHeight = height + softness * 4;
  const shapeWidth = width + softness * 2;
  const shapeHeight = height + softness * 2;
  const borderRadius = getCornerRadii(
    element.borderRadius,
    softness,
    shapeWidth,
    shapeHeight
  );
  const path = getRoundedRectPath({
    x: softness,
    y: softness,
    width: shapeWidth,
    height: shapeHeight,
    borderRadius,
  });
  const originalPath = getRoundedRectPath({
    x: softness * 2,
    y: softness * 2,
    width,
    height,
    borderRadius: getCornerRadii(element.borderRadius, 0, width, height),
  });
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${maskWidth} ${maskHeight}" preserveAspectRatio="none">`,
    '<defs>',
    `<filter id="soften" filterUnits="userSpaceOnUse" x="0" y="0" width="${maskWidth}" height="${maskHeight}">`,
    `<feGaussianBlur stdDeviation="${softness / 2}"/>`,
    '</filter>',
    '</defs>',
    `<path d="${path}" fill="black" filter="url(#soften)"/>`,
    `<path d="${originalPath}" fill="black"/>`,
    '</svg>',
  ].join('');

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function getBorderRadius(element, dataToStyleX, dataToStyleY) {
  const radii = getCornerRadii(
    element.borderRadius,
    0,
    normalizeDimension(element.width),
    normalizeDimension(element.height)
  );
  const xValues = [
    radii.topLeft,
    radii.topRight,
    radii.bottomRight,
    radii.bottomLeft,
  ].map(dataToStyleX);
  const yValues = [
    radii.topLeft,
    radii.topRight,
    radii.bottomRight,
    radii.bottomLeft,
  ].map(dataToStyleY);

  return `${xValues.join(' ')} / ${yValues.join(' ')}`;
}

export function getTextBoxBackdropStyles({
  element,
  dataToStyleX = (value) => `${value}px`,
  dataToStyleY = (value) => `${value}px`,
  dataToBlur = dataToStyleX,
  disableBlur = false,
}) {
  if (!hasTextBoxBackdrop(element)) {
    return null;
  }

  const blur = normalizeTextBoxBackdropValue(element.textBoxBackdropBlur);
  const softness = normalizeTextBoxBackdropValue(
    element.textBoxBackdropEdgeSoftness
  );
  const expansion = softness * 2;
  const style = {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 0,
    left: dataToStyleX(-expansion),
    top: dataToStyleY(-expansion),
    width: `calc(100% + ${dataToStyleX(expansion * 2)})`,
    height: `calc(100% + ${dataToStyleY(expansion * 2)})`,
    ...(disableBlur
      ? null
      : {
          backdropFilter: `blur(${dataToBlur(blur)})`,
          WebkitBackdropFilter: `blur(${dataToBlur(blur)})`,
        }),
  };

  if (softness > 0) {
    const maskImage = getSoftEdgeMask(element, softness);
    return {
      ...style,
      maskImage,
      WebkitMaskImage: maskImage,
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskSize: '100% 100%',
      WebkitMaskSize: '100% 100%',
    };
  }

  return {
    ...style,
    borderRadius: getBorderRadius(element, dataToStyleX, dataToStyleY),
    overflow: 'hidden',
  };
}
